import Drawer from '@navi/web-ui/lib/primitives/Drawer/Drawer';
import Button from '@navi/web-ui/lib/primitives/Button/Button';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Chip, Typography } from '@navi/web-ui/lib/primitives';
import styles from './index.module.scss';
import cx from 'classnames';
import TicketForm from '../TicketForm';
import {
  createTicket,
  fetchFormOptions,
  fetchRequestRaisedTickets,
  fetchRequestReceivedTickets
} from '../../actions/CsaTicketingActions';
import DrawerHeader from '../DrawerHeader';
import RaiseRequestIcon from '@cp/src/assets/icons/RaiseRequestIcon';
import { LoadingIcon } from '@navi/web-ui/lib/icons';
import { ICreateTicketDrawer, IFormData } from '../../types';
import { RootState } from '@cp/src/store';
import LineLoader from '@cp/src/components/SkeletonLoader/LineLoader';
import SuspenseLoader from '@cp/src/components/SkeletonLoader/SuspenseLoader';
import { CSA } from '@cp/src/pages/CsaCoordination/contants';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';

const CreateTicketDrawer = (props: ICreateTicketDrawer) => {
  const { toggleDrawer, isOpen } = props;

  const dispatch = useDispatch();
  const { loanId = '' } = useParams();

  const userId = useSelector((state: RootState) => state?.common?.userData?.referenceId) || '';

  const [showSubmitLoader, setShowSubmitLoader] = useState(false);
  const [formData, setFormData] = useState<IFormData>({
    requestTypes: [],
    subTitle: ''
  });
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    formState: { errors }
  } = useForm<any>({
    mode: 'onChange'
  });

  useEffect(() => {
    if (isOpen)
      fetchFormOptions(loanId, setLoading, (values, subTitle) => {
        setFormData({
          requestTypes: values,
          subTitle
        });
      });
    return () => {
      if (!isOpen) reset();
    };
  }, [isOpen]);

  const formValues = getValues('componentAdjustments');
  const requestType = watch('componentAdjustments.requestType');

  const handleRequestFormSubmit = () => {
    const payload = {
      supportRequestType: formValues?.requestType,
      supportRequestCreatorType: CSA,
      loanAccountNumber: loanId,
      address: formValues?.address ?? '',
      comment: formValues?.comment
    };
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CREATE_TASK_CLICKED);
    createTicket(payload, setShowSubmitLoader, () => {
      dispatch(fetchRequestReceivedTickets({ userId, loanId }));
      dispatch(fetchRequestRaisedTickets({ userId, loanId }));
      toggleDrawer();
    });
  };

  return (
    <form>
      <Drawer
        headerContainerClasses={styles.drawerHeaderContainer}
        open={isOpen}
        onClose={toggleDrawer}
        headerText={
          <DrawerHeader
            icon={<RaiseRequestIcon />}
            title="Create task for FE"
            subTitle={`To ${formData?.subTitle}`}
            loading={loading}
          />
        }
        footer={
          <Button
            fullWidth
            disabled={!requestType || showSubmitLoader}
            onClick={handleSubmit(handleRequestFormSubmit)}
          >
            {!showSubmitLoader ? 'Create task' : <LoadingIcon color="white" />}
          </Button>
        }
      >
        {isOpen && (
          <div className={styles.requestFormContainer}>
            <Typography className={styles.requestHeader} as="span" variant="h4">
              Task Type
            </Typography>
            <div className={styles.questions}>
              <SuspenseLoader
                loading={loading}
                fallBack={
                  <div className="flex flex-wrap flex-1 gap-3">
                    {[...Array(3)].map((type, index) => (
                      <LineLoader
                        key={index}
                        containerStyle="flex-1"
                        childrenType="H4"
                        lineStyle={cx(styles.lineLoader)}
                      />
                    ))}
                  </div>
                }
              >
                {formData?.requestTypes.map(request => (
                  <Chip
                    key={request.value}
                    label={request.label}
                    {...register(`componentAdjustments.requestType`, {})}
                    wrapperClasses={cx([
                      styles.chip,
                      { [styles.chipActiveColor]: request.value === requestType }
                    ])}
                    labelClasses={styles.text}
                    onChipClick={() => {
                      setValue('componentAdjustments.requestType', request.value);
                    }}
                  />
                ))}
              </SuspenseLoader>
            </div>
            {requestType ? (
              <TicketForm
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                register={register}
                errors={errors}
              />
            ) : null}
          </div>
        )}
      </Drawer>
    </form>
  );
};

export default CreateTicketDrawer;
